import { useEffect, useRef, useState } from "react";

import type { LogoutReason } from "@/constants/Login";
import type { ModalLockSource } from "@/hooks/useModalTracker";
import { ModalLockSources, useModalTracker } from "@/hooks/useModalTracker";
import useOnlyFromLoginPageLogout from "@/hooks/useOnlyFromLoginPageLogout";
import useAppMonitor from "@/services/AppMonitor";
import { useIsRootNavigatorReadySelector } from "@/store/reducers/appStartup";
import { useLogoutReasonSelector } from "@/store/reducers/auth";
import { toMs } from "@/utils/date.util";

const LoginInitTimeoutMs = toMs({ seconds: 10 });

interface UseLoginInitializationProps {
  canLoginInitializationBegin: boolean;
  onLogoutFinally?: (reason: LogoutReason) => Promise<void>;
}

const useLoginInitialization = ({
  canLoginInitializationBegin,
  onLogoutFinally,
}: UseLoginInitializationProps) => {
  const { logout } = useOnlyFromLoginPageLogout();
  const { allowNativeModalsAfterDelay } = useModalTracker();
  const { captureException, captureMessage } = useAppMonitor();
  const isRootNavigatorReady = useIsRootNavigatorReadySelector();
  const logoutReason = useLogoutReasonSelector();
  const initTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const [isInitializationComplete, setIsInitializationComplete] =
    useState<boolean>(false);
  const [hasIntercomLogoutFailed, setHasIntercomLogoutFailed] =
    useState<boolean>(false);
  const [hasLogoutInitTimedOut, setHasLogoutInitTimedOut] =
    useState<boolean>(false);
  const [hasRevokeAuthTokenFailed, setHasRevokeAuthTokenFailed] =
    useState<boolean>(false);
  const [hasClearCredentialsFailed, setHasClearCredentialsFailed] =
    useState<boolean>(false);

  const isRestartRequired =
    hasIntercomLogoutFailed ||
    hasLogoutInitTimedOut ||
    hasRevokeAuthTokenFailed ||
    hasClearCredentialsFailed;
  const isInitializationSuccess =
    isInitializationComplete && !isRestartRequired;
  const isInitializationLoading =
    !isInitializationComplete && !isRestartRequired;

  useEffect(() => {
    if (!canLoginInitializationBegin) return;

    const completeInitialization = () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      setIsInitializationComplete(true);
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Record type guarantees all values
      const allModalLockSources = Object.keys(
        ModalLockSources,
      ) as ModalLockSource[];
      allModalLockSources.forEach(allowNativeModalsAfterDelay);
    };

    initTimeoutRef.current = setTimeout(() => {
      setHasLogoutInitTimedOut(true);
      captureMessage(
        "Login initialization: logout has not completed within timeout; user must close/reopen app",
      );
      // Do not call completeInitialization() — user must close/reopen app (HIPAA).
      initTimeoutRef.current = null;
    }, LoginInitTimeoutMs);

    const asyncLogout = async () => {
      try {
        // If app is not yet ready, skip logout to avoid duplicate network requests (JN-1907).
        if (isRootNavigatorReady) {
          await logout({
            onClearCredentialsFailed: () => setHasClearCredentialsFailed(true),
            onIntercomLogoutFailed: () => setHasIntercomLogoutFailed(true),
            onRevokeAuthTokenFailed: () => setHasRevokeAuthTokenFailed(true),
            reason: logoutReason,
          });
        }
      } catch (err) {
        captureException(err, {
          context: {
            extra: {
              scenario: "Login initialization: logout threw before completing",
            },
          },
        });
      } finally {
        await onLogoutFinally?.(logoutReason);
        completeInitialization();
      }
    };

    void asyncLogout();

    return () => {
      if (!initTimeoutRef.current) return;
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    };

    // on mobile, always `canBegin: true`, so effectively an empty dependency array -- closure values at mount time
    // on web, `canBegin` must be delayed until ready, but all other dependencies are intentionally omitted
    // this tear down should execute ONCE, not re-fire if any dependencies change
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [canLoginInitializationBegin]);

  return {
    isInitializationLoading,
    isInitializationSuccess,
    isRestartRequired,
  };
};

export default useLoginInitialization;
